import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { InferResponseType } from 'hono/client';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '~/components/ui/select';
import { api } from '~/lib/api';
import { i18n } from '~/lib/i18n';

type DeptResponse = InferResponseType<(typeof api.api.departments)['$get']>;
type Department = DeptResponse['departments'][number];

const ASSIGNABLE_ROLES = ['org_manager', 'dept_manager', 'user'] as const;
type AssignableRole = (typeof ASSIGNABLE_ROLES)[number];

interface InviteDialogProps {
  orgId: string;
  departments: Department[];
}

export function InviteDialog({ orgId, departments }: InviteDialogProps): React.ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<AssignableRole>('user');
  const [selectedDeptIds, setSelectedDeptIds] = useState<string[]>([]);

  const invite = useMutation({
    mutationFn: async () => {
      const res = await api.api.org[':orgId'].users.invite.$post({
        param: { orgId },
        json: { email, role, departmentIds: selectedDeptIds }
      });
      if (!res.ok) throw new Error(i18n.t('errors.unexpected'));
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['team'] });
      setOpen(false);
      setEmail('');
      setRole('user');
      setSelectedDeptIds([]);
    }
  });

  function toggleDept(id: string): void {
    setSelectedDeptIds((prev) =>
      prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger render={<Button>{t('team.invite')}</Button>} />
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('team.inviteDialog.title')}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-foreground">
              {t('team.inviteDialog.email')}
            </label>
            <Input
              type="email"
              placeholder="colleague@company.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
              }}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-foreground">
              {t('team.inviteDialog.role')}
            </label>
            <Select
              value={role}
              onValueChange={(v) => {
                setRole(v as AssignableRole);
              }}
            >
              <SelectTrigger>
                <SelectValue>{t(`team.roles.${role}`)}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {ASSIGNABLE_ROLES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {t(`team.roles.${r}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-foreground">
              {t('team.inviteDialog.departments')}
            </label>
            <div className="flex flex-col gap-1">
              {departments.map((dept) => (
                <label key={dept.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedDeptIds.includes(dept.id)}
                    onChange={() => {
                      toggleDept(dept.id);
                    }}
                    className="rounded border-border"
                  />
                  {dept.name}
                </label>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            disabled={!email || selectedDeptIds.length === 0 || invite.isPending}
            onClick={() => {
              invite.mutate();
            }}
          >
            {t('team.inviteDialog.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
